<!-- src/pages/HelpView.vue -->
<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useOnboardingStore } from '../stores/onboarding'
import { useOnboardingChecklist, type ChecklistItem } from '../composables/useOnboardingChecklist'
import { HELP_FEATURES } from '../config/onboardingTour'

const router = useRouter()
const authStore = useAuthStore()
const store = useOnboardingStore()

const { items, completedCount, total, progress } = useOnboardingChecklist()

const userId = computed(() => (authStore.currentUser as { id?: string | number } | null)?.id ?? null)
const firstName = computed(() => authStore.currentUser?.first_name || 'there')

const startTour = () => store.replay()

const showMe = (item: ChecklistItem) => store.startAtStep(item.tourStepId)

const goTo = (route: string) => router.push(route)

const showChecklist = () => {
  store.restoreChecklist(userId.value)
  store.checklistExpanded = true
}
</script>

<template>
  <div class="page-enter help-container">
    <!-- Header -->
    <header class="help-header">
      <p class="help-eyebrow">Help &amp; getting started</p>
      <h1 class="font-display help-title">Welcome to Meal Mate, {{ firstName }}</h1>
      <p class="help-lede">
        Plan meals on a calendar, keep a recipe collection, auto-generate shopping lists from your
        plan, and share it all with your household. Here’s how to make the most of it.
      </p>
      <div class="help-header-actions">
        <button class="ds-btn-primary help-btn-icon" @click="startTour">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" stroke-linejoin="round"><polygon points="5 3 19 12 5 21 5 3" /></svg>
          Start the guided tour
        </button>
        <button class="ds-btn-secondary" @click="showChecklist">Show my checklist</button>
      </div>
    </header>

    <!-- Getting started checklist -->
    <section class="help-section">
      <div class="help-section-head">
        <div>
          <h2 class="font-display help-section-title">Your first 5 minutes</h2>
          <p class="help-section-sub">Follow these four steps to set up your kitchen. They tick off as you go.</p>
        </div>
        <div class="help-progress">
          <span class="help-progress-count">{{ completedCount }}/{{ total }}</span>
          <div class="help-progress-track"><div class="help-progress-fill" :style="{ width: progress + '%' }" /></div>
        </div>
      </div>

      <ol class="help-steps">
        <li v-for="(item, i) in items" :key="item.id" class="ds-card help-step" :class="{ 'help-step--done': item.done }">
          <div class="help-step-marker">
            <svg v-if="item.done" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
            <span v-else>{{ i + 1 }}</span>
          </div>
          <div class="help-step-body">
            <div class="help-step-top">
              <h3 class="help-step-label">{{ item.label }}</h3>
              <span v-if="item.done" class="help-badge help-badge--done">Done</span>
            </div>
            <p class="help-step-details">{{ item.details }}</p>
            <div class="help-step-actions">
              <button class="ds-btn-primary help-step-cta" @click="goTo(item.route)">{{ item.cta }}</button>
              <button class="help-link-btn" @click="showMe(item)">Show me in the app →</button>
            </div>
          </div>
        </li>
      </ol>
    </section>

    <!-- Explore the app -->
    <section class="help-section">
      <div class="help-section-head">
        <div>
          <h2 class="font-display help-section-title">Explore the app</h2>
          <p class="help-section-sub">Everything Meal Mate can do, area by area.</p>
        </div>
      </div>

      <div class="help-grid">
        <article v-for="feature in HELP_FEATURES" :key="feature.id" class="ds-card help-feature">
          <div class="help-feature-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
              stroke-linecap="round" stroke-linejoin="round"><path :d="feature.icon" /></svg>
          </div>
          <h3 class="help-feature-title">{{ feature.title }}</h3>
          <p class="help-feature-summary">{{ feature.summary }}</p>
          <ul class="help-feature-points">
            <li v-for="(point, idx) in feature.points" :key="idx">{{ point }}</li>
          </ul>
          <button class="help-link-btn" @click="goTo(feature.route)">{{ feature.linkLabel }} →</button>
        </article>
      </div>
    </section>
  </div>
</template>

<style scoped>
.help-container {
  max-width: 980px;
  margin: 0 auto;
  padding: 40px 24px 80px;
}

/* ── Header ── */
.help-header {
  margin-bottom: 48px;
}
.help-eyebrow {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--color-primary-500, #4f8646);
  margin: 0 0 8px;
}
.help-title {
  font-size: 34px;
  letter-spacing: -1px;
  color: #0f172a;
  margin: 0 0 12px;
}
.help-lede {
  font-size: 15px;
  line-height: 1.6;
  color: #64748b;
  max-width: 640px;
  margin: 0 0 24px;
}
.help-header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
.help-btn-icon {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

/* ── Sections ── */
.help-section {
  margin-bottom: 48px;
}
.help-section-head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.help-section-title {
  font-size: 22px;
  letter-spacing: -0.5px;
  color: #0f172a;
  margin: 0;
}
.help-section-sub {
  font-size: 14px;
  color: #64748b;
  margin: 6px 0 0;
}
.help-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}
.help-progress-count {
  font-size: 13px;
  font-weight: 700;
  color: #334155;
}
.help-progress-track {
  width: 120px;
  height: 6px;
  background: #eef2f6;
  border-radius: 999px;
  overflow: hidden;
}
.help-progress-fill {
  height: 100%;
  background: var(--color-primary-500, #4f8646);
  border-radius: 999px;
  transition: width 0.3s ease;
}

/* ── Steps ── */
.help-steps {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.help-step {
  display: flex;
  gap: 16px;
  padding: 20px 22px;
}
.help-step-marker {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-600, #3f6e38);
  display: grid;
  place-items: center;
  font-size: 14px;
  font-weight: 700;
}
.help-step--done .help-step-marker {
  background: var(--color-primary-500, #4f8646);
  color: #fff;
}
.help-step-body {
  flex: 1;
  min-width: 0;
}
.help-step-top {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 6px;
}
.help-step-label {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}
.help-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
}
.help-badge--done {
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-600, #3f6e38);
}
.help-step-details {
  font-size: 14px;
  line-height: 1.6;
  color: #64748b;
  margin: 0 0 14px;
}
.help-step-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.help-step-cta {
  padding: 8px 16px;
  font-size: 13px;
}

/* ── Feature grid ── */
.help-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 16px;
}
.help-feature {
  padding: 22px;
  display: flex;
  flex-direction: column;
}
.help-feature-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: var(--color-primary-50, #edf3ec);
  color: var(--color-primary-600, #3f6e38);
  display: grid;
  place-items: center;
  margin-bottom: 12px;
}
.help-feature-title {
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 6px;
}
.help-feature-summary {
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
  margin: 0 0 14px;
}
.help-feature-points {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
}
.help-feature-points li {
  position: relative;
  padding-left: 20px;
  font-size: 13px;
  line-height: 1.45;
  color: #475569;
}
.help-feature-points li::before {
  content: '';
  position: absolute;
  left: 4px;
  top: 7px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--color-primary-400, #6aad5e);
}

.help-link-btn {
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-primary-600, #3f6e38);
}
.help-link-btn:hover {
  text-decoration: underline;
}

@media (max-width: 640px) {
  .help-title {
    font-size: 27px;
  }
}
</style>
